import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { DATE_FORMATS } from '@/utils/date-formats';
import { compareDesc, format, isValid, parse, parseISO } from 'date-fns';
import type { CoachNote } from './types';

const MAX_VISIBLE_NOTES = 5;

function getGuardianPlayerName(player: GuardianPlayer) {
  return [player.firstname, player.lastname]
    .map(name => (name ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

function parseNoteDate(raw: string): Date {
  const trimmed = raw.trim();
  if (!trimmed) return new Date(0);

  const iso = parseISO(trimmed);
  if (isValid(iso)) return iso;

  const normalized = trimmed.replace(/,(\d{4})$/, ', $1');
  const parsed = parse(normalized, 'EEEE, MMMM d, yyyy', new Date());
  return isValid(parsed) ? parsed : new Date(0);
}

export function formatCoachNoteDate(raw: string) {
  const date = parseNoteDate(raw);
  if (date.getTime() <= 0) return raw.trim().replace(/,(\d{4})$/, ', $1');
  return format(date, DATE_FORMATS.TEXT_WEEKDAY);
}

export function getCoachNotesFromGuardianPlayers(
  guardianPlayers: GuardianPlayer[] | undefined,
): CoachNote[] {
  const notesWithParsedDate = (guardianPlayers ?? []).flatMap(player => {
    const playerName = getGuardianPlayerName(player);
    return (player.notes ?? [])
      .filter(note => note.coachName.trim().length > 0)
      .map(note => ({ note: { ...note, playerName }, parsedDate: parseNoteDate(note.date) }));
  });

  return notesWithParsedDate
    .sort((a, b) => compareDesc(a.parsedDate, b.parsedDate))
    .slice(0, MAX_VISIBLE_NOTES)
    .map(({ note }) => note);
}
